import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { PageLayout, ActionButton, ImageUpload } from "../../../components/modern";
import { adminApi } from "../../../utils/api";
import { getApiUrl } from "../../../config/env";
import { showErrorToast, showSuccessToast } from "../../../utils/toastNotifications";

export const CreateSlider: React.FC = () => {
  const { t, i18n } = useTranslation(["common"]);
  const navigate = useNavigate();
  const isRTL = i18n.language === "ar";

  const [formData, setFormData] = useState<{
    webImage: File | null;
    mobileImage: File | null;
    displayOrder: number;
    isActive: boolean;
  }>({
    webImage: null,
    mobileImage: null,
    displayOrder: 0,
    isActive: true
  });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : type === "number" ? Number(value) : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.webImage || !formData.mobileImage) {
      showErrorToast("Both Web and Mobile images are required");
      return;
    }

    const data = new FormData();
    data.append('WebImage', formData.webImage);
    data.append('MobileImage', formData.mobileImage);
    data.append('DisplayOrder', formData.displayOrder.toString());
    data.append('IsActive', formData.isActive.toString());

    setLoading(true);
    try {
      const response = await fetch(getApiUrl('/slider'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: data
      });

      if (!response.ok) {
        const error = await response.text();
        showErrorToast(error || "Failed to create slider");
        return;
      }

      showSuccessToast("Slider created successfully");
      navigate("/cms/sliders");
    } catch (error) {
      showErrorToast("Error creating slider");
      console.error("Error creating slider:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout
      title="Add New Slider"
      subtitle="Create a new hero slider for the website"
      breadcrumbs={[{ label: "CMS" }, { label: "Sliders", path: "/cms/sliders" }, { label: "Add" }]}
      className={isRTL ? "rtl" : "ltr"}
    >
      <div className="card shadow-lg border-0 rounded-3 p-4">
        <form onSubmit={handleSubmit}>
          <div className="row g-4">
            <div className="col-md-6">
              <ImageUpload
                label="Web Image (Wide)"
                value={formData.webImage}
                onChange={(file) => setFormData((prev) => ({ ...prev, webImage: file }))}
                placeholder="Upload web version (e.g. 1920x600)"
              />
            </div>
            <div className="col-md-6">
              <ImageUpload
                label="Mobile Image (Tall/Square)"
                value={formData.mobileImage}
                onChange={(file) => setFormData((prev) => ({ ...prev, mobileImage: file }))}
                placeholder="Upload mobile version (e.g. 800x800)"
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Display Order</label>
              <input
                type="number"
                className="form-control"
                name="displayOrder"
                value={formData.displayOrder}
                onChange={handleInputChange}
              />
              <div className="form-text">Lower numbers appear first.</div>
            </div>

            <div className="col-md-6 d-flex align-items-end">
              <div className="form-check form-switch mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                />
                <label className="form-check-label" htmlFor="isActive">
                  Active (Show on website)
                </label>
              </div>
            </div>
          </div>

          <div className="text-center mt-5">
            <ActionButton
              variant="secondary"
              onClick={() => navigate("/cms/sliders")}
              className={isRTL ? "ms-2" : "me-2"}
              type="button"
            >
              Cancel
            </ActionButton>
            <ActionButton
              variant="primary"
              type="submit"
              loading={loading}
              disabled={loading}
            >
              Create Slider
            </ActionButton>
          </div>
        </form>
      </div>
    </PageLayout>
  );
};
